import Stripe from "stripe";
import { z } from "zod/v4";

import { OrgShopIdDtoSchema } from "../dtos/organization-shop-id";
import { StripeSubscriptionStatus } from "../dtos/organization-subscriptions";
import { StripeEntitlementFeatureMetadata } from "../dtos/stripe-api";
import { UpcomingPriceChangeDto } from "../dtos/stripe-subscription-links";
import {
  StripeInterval,
  SubscriptionBillingProviderList,
} from "../dtos/subscriptions";
import { castToBool, castToNumber } from "../utils";

export const StripeSubscriptionIntervalList = [
  "day",
  "month",
  "week",
  "year",
] as const;

export type StripeSubscriptionInterval = Stripe.Price.Recurring.Interval;

export const CouponSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  amountOff: z.number().nullish(),
  percentOff: z.number().nullish(),
});

export type CouponType = z.infer<typeof CouponSchema>;

export type StripeMeterEventPayload = {
  value: number;
  customerId: string;
};

export type StripeMonth =
  `${number}-${"01" | "02" | "03" | "04" | "05" | "06" | "07" | "08" | "09" | "10" | "11" | "12"}`;

export const STRIPE_LATEST_PRODUCT_VERSION = "v3";

export const StripeProductVersionList = ["v1", "v2", "v3"] as const;
export type StripeProductVersion = (typeof StripeProductVersionList)[number];
export const StripeNewFormatVersionList: StripeProductVersion[] = [
  "v2",
  "v3",
] as const;
export interface StripeEntitlementFeature extends Stripe.Entitlements.Feature {
  metadata: StripeEntitlementFeatureMetadata;
}

export interface StripeProductFeature extends Stripe.ProductFeature {
  entitlement_feature: StripeEntitlementFeature;
}

export const StripeInvoiceMetadataSchema = z.object({
  billingProvider: z.enum(SubscriptionBillingProviderList),
  billingApproved: castToBool,
  billingEverApproved: castToBool.nullish(),
  mistakenlyMarkedPaid: castToBool.nullish(),
});

export type StripeInvoiceMetadata = z.infer<typeof StripeInvoiceMetadataSchema>;

export type StripeInvoice = Stripe.Invoice & {
  id: string;
};

export const StripeSubscriptionMetadataSchema = z.object({
  billingProvider: z.enum(SubscriptionBillingProviderList),
  billingApproved: castToBool,
  billingEverApproved: castToBool.nullish(),
  orgs: OrgShopIdDtoSchema.array(),
  autoScale: castToBool.optional(),
  version: z.enum(StripeProductVersionList),
  hideMissingPaymentAlert: z.string().nullish(),
  shopifyAppSubscriptionId: z.string().optional(),
  agencyEmails: z.string().array().nullish(),
  disableShopifyChargesUntil: z.string().nullish(),
});

export type StripeSubscriptionMetadata = z.infer<
  typeof StripeSubscriptionMetadataSchema
>;

export const StripeProductMetadataSchema = z.object({
  level: castToNumber.nullish().default(-1),
  version: z.enum(StripeProductVersionList),
  features: z.string().array(),
  maxOrderCount: castToNumber.nullish(),
});

export type StripeProductMetadata = z.infer<typeof StripeProductMetadataSchema>;

export const StripeApiPriceTemplateDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  tierMap: z.record(z.string(), z.string().array()),
  liveTemplate: castToBool,
});

export type StripeApiPriceTemplateDto = z.infer<
  typeof StripeApiPriceTemplateDtoSchema
>;

export const StripePriceMetadataSchema = z.object({
  // isLive: castToNumber.nullish(),
  comparePrice: castToNumber.nullish(),
  templates: StripeApiPriceTemplateDtoSchema.array(),
  tier: z.string().nullish(),
  version: z.string().nullish(),
  active: castToBool.nullish(),
  freeTrialDays: castToNumber.nullish(),
});

export type StripePriceMetadata = z.infer<typeof StripePriceMetadataSchema>;

export const StripeSubscriptionItemMetadataSchema = z.object({
  defaultQuantity: castToNumber,
});

export type StripeSubscriptionItemMetadata = z.infer<
  typeof StripeSubscriptionItemMetadataSchema
>;
export type StripeSubscriptionItem = Omit<
  Stripe.SubscriptionItem,
  "metadata" | "price"
> & {
  price: StripePrice;
  metadata: StripeSubscriptionItemMetadata;
};

export type StripeSubscriptionSchedulePhaseItem = Omit<
  Stripe.SubscriptionSchedule.Phase.Item,
  "metadata"
> & {
  metadata: StripeSubscriptionItemMetadata;
};

export type StripeSubscriptionSchedulePhase = Omit<
  Stripe.SubscriptionSchedule.Phase,
  "items"
> & {
  items: StripeSubscriptionSchedulePhaseItem[];
};

export type StripeSubscriptionSchedule = Omit<
  Stripe.SubscriptionSchedule,
  "phases"
> & {
  phases: StripeSubscriptionSchedulePhase[];
};

export type CachedStripePrice = {
  active: boolean;
  stripePriceId: string;
  stripeProductId: string;
  nickname: string | null;
  productName: string;
  unitAmount: number | null;
  metadata: StripePriceMetadata;
  tiers: Stripe.Price.Tier[] | undefined;
};

export type CachedStripeCustomer = {
  id: string;
  name: string;
  email?: string | null;
  billing: boolean;
  hasActiveStripeSubscription: boolean;
  hasActiveShopifySubscription: boolean;
};

export type CachedStripeSubscription = {
  partitionKey: string;
  stripeSubscriptionId: string;
  orgId: string | null;
  periodStart: number;
  periodEnd: number;
  items: StripePhaseOrLineItemItems;
  cancelAt: number | null;
  stripeSubscriptionScheduleId: string | null;
  upcomingPrices: UpcomingPriceChangeDto[];
  endingPrices: UpcomingPriceChangeDto[];
  agencyEmails?: string[] | null;
  metadata: StripeSubscriptionMetadata;
  interval: StripeInterval;
  intervalCount: number;
  isAgencySub: boolean;
  status: StripeSubscriptionStatus;
  stripePriceId: string;
  version: string | null | undefined;
  coupon?: {
    percentOff?: number;
    amountOff?: number;
  };
  customer: {
    id: string;
    billing: boolean;
  };
};

export type StripeSubscription = Omit<
  Stripe.Subscription,
  "metadata" | "items" | "schedule"
> & {
  metadata: StripeSubscriptionMetadata;
  items: Stripe.ApiList<StripeSubscriptionItem>;
  schedule: StripeSubscriptionSchedule | null;
};

export type StripeProduct = Omit<Stripe.Product, "metadata"> & {
  metadata: StripeProductMetadata;
};

export type StripePrice = Omit<Stripe.Price, "metadata" | "product"> & {
  metadata: StripePriceMetadata;
  product: string | StripeProduct;
};

export interface StripeCustomerSubscriptionCreatedEvent {
  object: StripeSubscription;
  previous_attributes?: Partial<StripeSubscription>;
}

export type StripePhaseOrLineItemItem =
  | StripeSubscriptionItem
  | StripeSubscriptionSchedulePhaseItem;
export type StripePhaseOrLineItemItems =
  | StripeSubscriptionItem[]
  | StripeSubscriptionSchedulePhaseItem[];
